import { useState, useRef, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Image, FileCode, File, CheckCircle, Clock, Loader2, AlertCircle, MoreHorizontal, MoveRight, FileX, Puzzle, ShieldAlert, ArrowUpFromLine } from 'lucide-react'
import type { Document } from '../../types'
import { updateDocument, promoteDocument } from '../../services/api'
import { useRole } from '../../contexts/RoleContext'
import MoveDocumentModal from './MoveDocumentModal'

interface DocumentCardProps {
  document: Document
  linkPrefix?: string
  variant?: 'list' | 'grid'
}

// BUG-021 (1C-06): long MIME types display as raw strings (e.g. OOXML produces
// "VND.OPENXMLFORMATS-OFFICEDOCUMENT.PRESENTATIONML.PRESENTATION"). Map to
// the short human-readable extension instead.
const MIME_TO_EXT: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-word': 'DOC',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
  'application/msword': 'DOC',
  'application/vnd.oasis.opendocument.presentation': 'ODP',
  'application/vnd.oasis.opendocument.text': 'ODT',
  'application/vnd.oasis.opendocument.spreadsheet': 'ODS',
  'text/csv': 'CSV',
  'application/json': 'JSON',
  'application/xml': 'XML',
  'application/zip': 'ZIP',
}

function contentTypeLabel(contentType?: string): string {
  if (!contentType) return 'FILE'
  if (MIME_TO_EXT[contentType]) return MIME_TO_EXT[contentType]
  return contentType.split('/').pop()?.toUpperCase() || 'FILE'
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  'application/pdf': <FileText size={16} className="text-red-500" />,
  'image/png': <Image size={16} className="text-green-500" />,
  'image/jpeg': <Image size={16} className="text-green-500" />,
  'text/plain': <FileCode size={16} className="text-gray-500" />,
}

const EMBEDDING_INDICATORS: Record<string, React.ReactNode> = {
  complete: <CheckCircle size={12} className="text-green-500" />,
  pending: <Clock size={12} className="text-yellow-500" />,
  processing: <Loader2 size={12} className="text-iflow-500 animate-spin" />,
  failed: <AlertCircle size={12} className="text-red-500" />,
}

const DOC_TYPE_LABELS: Record<string, string> = {
  patent_spec: 'Patent',
  financial: 'Financial',
  research_paper: 'Research',
  presentation: 'Presentation',
  '3d_model': '3D Model',
  technical_document: 'Technical',
  tabular_data: 'Data',
  image: 'Image',
}

const QUALITY_COLORS: Record<string, string> = {
  good: 'bg-green-400',
  none: 'bg-gray-300',
  poor: 'bg-yellow-400',
  unreadable: 'bg-red-400',
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function DocumentCard({ document: doc, linkPrefix = '', variant = 'list' }: DocumentCardProps) {
  const navigate = useNavigate()
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const { isAdmin } = useRole()
  const [showMenu, setShowMenu] = useState(false)
  const [showMove, setShowMove] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const isReference = doc.component_role === 'reference'
  const isQuarantined = doc.status === 'quarantined'
  const toggleReferenceMutation = useMutation({
    mutationFn: () =>
      updateDocument(doc.document_id, { component_role: isReference ? 'invention' : 'reference' } as Partial<Document>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', doc.project_id] })
      queryClient.invalidateQueries({ queryKey: ['documents', doc.project_id, 'with-orphans'] })
      setShowMenu(false)
    },
  })

  const promoteMutation = useMutation({
    mutationFn: () => promoteDocument(doc.document_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', doc.project_id] })
      queryClient.invalidateQueries({ queryKey: ['documents', doc.project_id, 'with-orphans'] })
      setShowMenu(false)
    },
  })

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    if (showMenu) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  const fileSizeStr = formatFileSize(doc.file_size)

  if (variant === 'grid') {
    return (
      <>
        <div
          onClick={() => navigate(`${linkPrefix}/document/${doc.document_id}`)}
          className="light-card p-3 hover:border-iflow-300 hover:shadow-sm transition-all cursor-pointer flex flex-col gap-2"
          data-testid="document-card-grid"
        >
          {/* Thumbnail area */}
          <div className="relative w-full aspect-square rounded-lg bg-sky-500/5 flex items-center justify-center overflow-hidden">
            <div className="flex flex-col items-center gap-1">
              {TYPE_ICONS[doc.content_type] || <File size={28} className="text-gray-300" />}
              <span className="text-xs text-gray-500 font-mono">{contentTypeLabel(doc.content_type)}</span>
            </div>
            {/* Status badges overlaid on thumbnail */}
            <div className="absolute top-1 right-1 flex flex-col gap-1">
              {isQuarantined && (
                <span className="p-0.5 bg-amber-100 text-amber-700 rounded" title="Pending Approval">
                  <ShieldAlert size={10} />
                </span>
              )}
              {doc.s3_orphaned && (
                <span className="p-0.5 bg-red-100 text-red-700 rounded" title="S3 file missing">
                  <FileX size={10} />
                </span>
              )}
            </div>
            {/* Context menu top-left */}
            <div ref={menuRef} className="absolute top-1 left-1">
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu) }}
                className="p-0.5 text-gray-500 hover:text-gray-600 rounded bg-white/70 hover:bg-white"
              >
                <MoreHorizontal size={14} />
              </button>
              {showMenu && (
                <div className="absolute left-0 top-6 light-card shadow-lg py-1 z-10 w-52">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); setShowMove(true) }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-white/30"
                  >
                    <MoveRight size={14} /> Move to...
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleReferenceMutation.mutate() }}
                    disabled={toggleReferenceMutation.isPending}
                    title="Reference parts are excluded from AI analysis."
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-white/30 disabled:opacity-50"
                  >
                    {toggleReferenceMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Puzzle size={14} />}
                    {isReference ? 'Unmark as Reference' : 'Mark as Reference Part'}
                  </button>
                  {isQuarantined && isAdmin() && (
                    <button
                      onClick={(e) => { e.stopPropagation(); promoteMutation.mutate() }}
                      disabled={promoteMutation.isPending}
                      title="Approve this collaborator document"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                    >
                      {promoteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpFromLine size={14} />}
                      Approve &amp; Promote
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Title + metadata */}
          <div className="min-w-0">
            <h4 className="text-xs font-medium text-gray-900 truncate" title={doc.title}>{doc.title}</h4>
            <div className="flex items-center gap-1 mt-0.5">
              {fileSizeStr && <span className="text-xs text-gray-500">{fileSizeStr}</span>}
              {doc.embedding_status && (
                <span title={`Indexing: ${doc.embedding_status}`}>
                  {EMBEDDING_INDICATORS[doc.embedding_status]}
                </span>
              )}
              {doc.text_quality_flag && QUALITY_COLORS[doc.text_quality_flag] && (
                <span
                  className={`w-2 h-2 rounded-full ${QUALITY_COLORS[doc.text_quality_flag]}`}
                  title={`Text quality: ${doc.text_quality_flag}`}
                />
              )}
            </div>
          </div>
        </div>
        {showMove && projectId && (
          <MoveDocumentModal
            documentIds={[doc.document_id]}
            currentFolderId={doc.folder_id || null}
            projectId={projectId}
            onClose={() => setShowMove(false)}
          />
        )}
      </>
    )
  }

  return (
    <>
      <div
        onClick={() => navigate(`${linkPrefix}/document/${doc.document_id}`)}
        className="light-card p-4 hover:border-iflow-300 hover:shadow-sm transition-all cursor-pointer flex items-center gap-4"
        data-testid="document-card-list"
      >
        <div className="w-10 h-10 rounded-lg bg-sky-500/5 flex items-center justify-center flex-shrink-0">
          {TYPE_ICONS[doc.content_type] || <File size={16} className="text-gray-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-gray-900 truncate">{doc.title}</h4>
            <span className="text-xs text-gray-500 flex-shrink-0">
              {contentTypeLabel(doc.content_type)}
            </span>
            {fileSizeStr && (
              <span className="hidden sm:inline text-xs text-gray-500 flex-shrink-0">{fileSizeStr}</span>
            )}
            {doc.created_at && (
              <span className="hidden sm:inline text-xs text-gray-500 flex-shrink-0">{formatDate(doc.created_at)}</span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {doc.summary ? doc.summary.slice(0, 80) + (doc.summary.length > 80 ? '...' : '') : doc.description}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Stream 7 (#3559): quarantined collaborator uploads show a badge. */}
          {isQuarantined && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-200"
              title="Uploaded by a collaborator — pending org admin approval"
            >
              <ShieldAlert size={12} />
              Pending Approval
            </span>
          )}
          {/* BUG-3221-FIX-S2D: surface s3_orphaned docs with a "Missing file"
              badge — health-check Lambda flagged the underlying S3 object as
              gone. Inventor sees the doc is unrecoverable without re-upload. */}
          {doc.s3_orphaned && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded-full border border-red-200"
              title={doc.orphaned_at ? `S3 file missing since ${doc.orphaned_at}` : 'S3 file missing'}
            >
              <FileX size={12} />
              Missing file
            </span>
          )}
          {doc.document_type && DOC_TYPE_LABELS[doc.document_type] && (
            <span className="hidden sm:inline-flex px-2 py-0.5 bg-teal-50 text-teal-600 text-xs rounded-full">
              {DOC_TYPE_LABELS[doc.document_type]}
            </span>
          )}
          {doc.text_quality_flag && QUALITY_COLORS[doc.text_quality_flag] && (
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${QUALITY_COLORS[doc.text_quality_flag]}`}
              title={`Text quality: ${doc.text_quality_flag}`}
            />
          )}
          {doc.domain && (
            <span className="hidden sm:inline-flex px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full">
              {doc.domain}
            </span>
          )}
          {doc.embedding_status && (
            <span className="flex items-center gap-1 text-xs text-gray-500" title={`Indexing: ${doc.embedding_status === 'complete' || doc.embedding_status === 'completed' ? 'ready for analysis' : doc.embedding_status === 'processing' ? 'in progress' : doc.embedding_status === 'pending' ? 'queued' : doc.embedding_status === 'failed' ? 'needs attention' : doc.embedding_status}`}>
              {EMBEDDING_INDICATORS[doc.embedding_status]}
            </span>
          )}
          {doc.topics && doc.topics.length > 0 && (
            <span className="hidden sm:inline-flex px-2 py-0.5 bg-iflow-50 text-iflow-600 text-xs rounded-full">
              {doc.topics[0]}{doc.topics.length > 1 && ` +${doc.topics.length - 1}`}
            </span>
          )}
          {/* Context menu */}
          <div ref={menuRef} className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu) }}
              className="p-1 text-gray-500 hover:text-gray-600 rounded hover:bg-sky-500/10"
            >
              <MoreHorizontal size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-8 light-card shadow-lg py-1 z-10 w-52">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMenu(false); setShowMove(true) }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-white/30"
                >
                  <MoveRight size={14} /> Move to...
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleReferenceMutation.mutate() }}
                  disabled={toggleReferenceMutation.isPending}
                  title="Reference parts (standard components like bolts or hex nuts) are excluded from AI synthesis and novelty analysis."
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-white/30 disabled:opacity-50"
                >
                  {toggleReferenceMutation.isPending
                    ? <Loader2 size={14} className="animate-spin" />
                    : <Puzzle size={14} />}
                  {isReference ? 'Unmark as Reference' : 'Mark as Reference Part'}
                </button>
                {/* Stream 7 (#3559): org admins can promote quarantined collaborator docs */}
                {isQuarantined && isAdmin() && (
                  <button
                    onClick={(e) => { e.stopPropagation(); promoteMutation.mutate() }}
                    disabled={promoteMutation.isPending}
                    title="Approve this collaborator document: copy to uploads prefix and trigger embedding"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                  >
                    {promoteMutation.isPending
                      ? <Loader2 size={14} className="animate-spin" />
                      : <ArrowUpFromLine size={14} />}
                    Approve &amp; Promote
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showMove && projectId && (
        <MoveDocumentModal
          documentIds={[doc.document_id]}
          currentFolderId={doc.folder_id || null}
          projectId={projectId}
          onClose={() => setShowMove(false)}
        />
      )}
    </>
  )
}
